import type { ContentReport } from '@pulsify/shared'
import { CheckCircle2, Flag, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { ContentReportStatus } from '~/entities/content-report/api/content-reports'
import {
  useContentReports,
  useDeleteContentReport,
  useResolveContentReport,
} from '~/entities/content-report/model/use-content-reports'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { getEventShareUrl, getListingShareUrl } from '~/shared/lib/share'
import { cn } from '~/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'

const REASON_LABELS: Record<ContentReport['reason'], () => string> = {
  inaccurate_data: m.contentReportReasonInaccurateData,
  inappropriate: m.contentReportReasonInappropriate,
  other: m.contentReportReasonOther,
}

function entityUrl(report: ContentReport): string {
  return report.entityType === 'listing'
    ? getListingShareUrl(report.entityId)
    : getEventShareUrl(report.entityId)
}

export function ContentReportsPage() {
  const [status, setStatus] = useState<ContentReportStatus>('open')
  const { data: reports, isLoading, error } = useContentReports(status)
  const resolveReport = useResolveContentReport()
  const deleteReport = useDeleteContentReport()
  const [reportToDelete, setReportToDelete] = useState<ContentReport | null>(null)

  return (
    <div className="p-4 py-8 sm:p-8 sm:py-24">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <h1 className="mb-2 font-bold text-slate-900 dark:text-slate-100 text-3xl">
            {m.contentReports()}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">{m.contentReportsDescription()}</p>
        </div>

        <div className="flex gap-2 mb-6">
          {(['open', 'resolved'] as const).map((tab) => (
            <Button
              key={tab}
              variant={status === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatus(tab)}
              className={cn(status === tab && 'bg-blue-600 hover:bg-blue-700 text-white')}
            >
              {tab === 'open' ? m.contentReportsOpen() : m.contentReportsResolved()}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="border-blue-600 border-t-4 rounded-full w-12 h-12 animate-spin" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-sm text-destructive">{m.contentReportsLoadError()}</p>
            </CardContent>
          </Card>
        ) : !reports || reports.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-16">
              <Flag className="w-12 h-12 text-slate-400" />
              <p className="text-slate-600 dark:text-slate-400 text-sm">{m.noContentReports()}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-slate-200 dark:border-slate-800 border-b text-left text-slate-500 dark:text-slate-400">
                    <th className="px-4 py-3 font-medium">{m.contentReportsDate()}</th>
                    <th className="px-4 py-3 font-medium">{m.contentReportsType()}</th>
                    <th className="px-4 py-3 font-medium">{m.contentReportsEntity()}</th>
                    <th className="px-4 py-3 font-medium">{m.contentReportsReason()}</th>
                    <th className="px-4 py-3 font-medium">{m.details()}</th>
                    <th className="px-4 py-3 font-medium text-right">
                      {m.contentReportsActions()}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {reports.map((report) => (
                    <tr key={report.id} className="align-top">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                        {new Date(report.createdAt).toLocaleString(getLocale(), {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={report.entityType === 'listing' ? 'secondary' : 'outline'}>
                          {report.entityType === 'listing'
                            ? m.contentReportTypeListing()
                            : m.contentReportTypeEvent()}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 max-w-48">
                        <a
                          href={entityUrl(report)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline break-words"
                        >
                          {report.entityName ?? report.entityId}
                        </a>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-900 dark:text-slate-100">
                        {REASON_LABELS[report.reason]()}
                      </td>
                      <td className="px-4 py-3 max-w-64 break-words text-slate-600 dark:text-slate-400">
                        {report.details ?? '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          {report.status === 'open' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resolveReport.mutate(report.id)}
                              disabled={resolveReport.isPending}
                            >
                              <CheckCircle2 className="mr-1 w-4 h-4" />
                              {m.contentReportResolve()}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReportToDelete(report)}
                            disabled={deleteReport.isPending}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="mr-1 w-4 h-4" />
                            {m.delete()}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <AlertDialog
          open={reportToDelete !== null}
          onOpenChange={(open) => {
            if (!open) setReportToDelete(null)
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{m.confirmDeleteContentReport()}</AlertDialogTitle>
              <AlertDialogDescription>{m.deleteContentReportWarning()}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (reportToDelete) deleteReport.mutate(reportToDelete.id)
                  setReportToDelete(null)
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {m.delete()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
